import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CheckCircle, Sparkle, Briefcase, Users, ArrowRight } from "@phosphor-icons/react";

/**
 * Iter 27 — Contractor → Core upgrade landing page. Stripe Checkout
 * returns here with ?session_id=cs_xxx. We sync the session (which
 * flips orgs.org_type=core when metadata.upgrade_to_core=true), then
 * play confetti, refresh /auth/me so the sidebar/permissions update
 * immediately, and offer a 1-click route to the Dashboard.
 */
export default function UpgradeSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [status, setStatus] = useState("syncing"); // syncing | success | already | error
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      setErr("Missing checkout session id.");
      return;
    }
    api.post(`/billing/sync/${sessionId}`)
      .then(async (r) => {
        await refresh?.();
        if (r.data.upgraded_to_core) {
          setStatus("success");
          fireConfetti();
        } else if (r.data.billing_active) {
          setStatus("already");
        } else {
          setStatus("error");
          setErr("Payment did not complete. Please try again.");
        }
      })
      .catch((e) => { setStatus("error"); setErr(formatErr(e)); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center p-8" data-testid="upgrade-success-page">
      <div className="surface max-w-xl w-full p-10 text-center">
        {status === "syncing" && (
          <>
            <div className="text-sm uppercase mono text-muted-foreground tracking-widest">PROCESSING</div>
            <h1 className="text-4xl font-semibold mt-3" data-testid="upgrade-syncing">Confirming your upgrade…</h1>
            <p className="text-sm text-muted-foreground mt-3">One moment while we activate your Core subscription.</p>
          </>
        )}

        {status === "success" && (
          <>
            <Sparkle size={36} weight="fill" className="mx-auto mb-2 text-blue-600" />
            <div className="text-sm uppercase mono text-blue-600 tracking-widest">WELCOME TO CORE</div>
            <h1 className="text-4xl font-semibold mt-3" data-testid="upgrade-success-heading">You&apos;re fully unlocked.</h1>
            <p className="text-base text-muted-foreground mt-4">
              Your organization just upgraded from Contractor to Core. Create your own jobs, manage your customer list, and assign work across the team — all open from this moment forward.
            </p>
            <div className="grid grid-cols-2 gap-3 mt-8 text-left">
              <Feature icon={Briefcase} label="Create your own jobs" />
              <Feature icon={Users} label="Manage customers" />
              <Feature icon={CheckCircle} label="Active subscription" />
              <Feature icon={CheckCircle} label="Per-job metered billing" />
            </div>
            <button
              className="btn btn-primary mt-8 w-full"
              onClick={() => navigate("/")}
              data-testid="upgrade-success-cta"
            >
              GO TO DASHBOARD <ArrowRight size={14} />
            </button>
          </>
        )}

        {status === "already" && (
          <>
            <CheckCircle size={36} weight="fill" className="mx-auto mb-2 text-green-600" />
            <h1 className="text-3xl font-semibold mt-3" data-testid="upgrade-already-active">Subscription already active</h1>
            <p className="text-sm text-muted-foreground mt-3">Your card is on file and billing is live. Nothing else to do.</p>
            <Link to="/settings" className="btn btn-secondary mt-6 inline-flex" data-testid="upgrade-go-billing">VIEW BILLING</Link>
          </>
        )}

        {status === "error" && (
          <>
            <div className="text-sm uppercase mono text-destructive tracking-widest">UPGRADE FAILED</div>
            <h1 className="text-3xl font-semibold mt-3" data-testid="upgrade-error">Something went wrong</h1>
            <p className="text-sm text-destructive mt-4" data-testid="upgrade-error-text">{err}</p>
            <Link to="/settings" className="btn btn-secondary mt-6 inline-flex" data-testid="upgrade-retry">RETURN TO BILLING</Link>
          </>
        )}
      </div>
    </div>
  );
}

const Feature = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 surface p-3">
    <Icon size={16} weight="duotone" className="text-blue-600 shrink-0" />
    <span className="text-sm">{label}</span>
  </div>
);

function fireConfetti() {
  const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };
  const interval = setInterval(() => {
    const particleCount = 50;
    confetti({ ...defaults, particleCount, origin: { x: Math.random(), y: Math.random() * 0.4 } });
  }, 250);
  setTimeout(() => clearInterval(interval), 2500);
  confetti({ ...defaults, particleCount: 150, origin: { y: 0.6 } });
}
